import type { Declaration, GlossaProgram, LValue, Statement } from '../parser/ast.js';

/**
 * Η ΔΙΑΒΑΣΕ δέχεται μόνο ΑΚΕΡΑΙΕΣ/ΠΡΑΓΜΑΤΙΚΕΣ/ΧΑΡΑΚΤΗΡΕΣ (σύμβαση βιβλίου)
 * — όχι ΛΟΓΙΚΕΣ, δεν υπάρχει τυπικός τρόπος να "πληκτρολογηθεί" μια λογική
 * τιμή. Βλ. `Interpreter.executeRead` για το runtime αντίστοιχο. Αυτός ο
 * έλεγχος τρέχει ΠΡΙΝ την εκτέλεση (ίδια λογική με `findWholeArrayAssignment`)
 * ώστε να πιάνει το λάθος ΑΚΟΜΑ ΚΙ αν βρίσκεται σε κλάδο/βρόχο που δεν θα
 * εκτελούνταν σε αυτό το συγκεκριμένο τρέξιμο.
 */
export interface ReadBooleanTarget {
  readonly name: string;
  readonly line: number;
  readonly column: number;
}

function isDeclaredBoolean(declarations: readonly Declaration[], name: string): boolean {
  for (const decl of declarations) {
    if (decl.kind === 'ConstantDeclaration') continue;
    for (const declarator of decl.declarators) {
      if (declarator.name === name) return decl.declaredType === 'ΛΟΓΙΚΕΣ';
    }
  }
  return false;
}

function checkTarget(target: LValue, declarations: readonly Declaration[]): ReadBooleanTarget | null {
  const name = target.kind === 'Identifier' ? target.name : target.array.name;
  if (!isDeclaredBoolean(declarations, name)) return null;
  return { name, line: target.line, column: target.column };
}

function checkStatement(stmt: Statement, declarations: readonly Declaration[]): ReadBooleanTarget | null {
  switch (stmt.kind) {
    case 'ReadStatement': {
      for (const target of stmt.targets) {
        const result = checkTarget(target, declarations);
        if (result !== null) return result;
      }
      return null;
    }
    case 'ForStatement':
      return checkStatements(stmt.body, declarations);
    case 'IfStatement': {
      const thenResult = checkStatements(stmt.thenBranch, declarations);
      if (thenResult !== null) return thenResult;
      for (const branch of stmt.elseIfBranches) {
        const branchResult = checkStatements(branch.body, declarations);
        if (branchResult !== null) return branchResult;
      }
      if (stmt.elseBranch !== null) return checkStatements(stmt.elseBranch, declarations);
      return null;
    }
    case 'WhileStatement':
      return checkStatements(stmt.body, declarations);
    case 'RepeatUntilStatement':
      return checkStatements(stmt.body, declarations);
    case 'SwitchStatement': {
      for (const switchCase of stmt.cases) {
        const result = checkStatements(switchCase.body, declarations);
        if (result !== null) return result;
      }
      if (stmt.elseBody !== null) return checkStatements(stmt.elseBody, declarations);
      return null;
    }
    case 'AssignmentStatement':
    case 'WriteStatement':
    case 'SwapStatement':
    case 'CallStatement':
      return null;
  }
}

function checkStatements(statements: readonly Statement[], declarations: readonly Declaration[]): ReadBooleanTarget | null {
  for (const stmt of statements) {
    const result = checkStatement(stmt, declarations);
    if (result !== null) return result;
  }
  return null;
}

/**
 * Επιστρέφει την ΠΡΩΤΗ ΔΙΑΒΑΣΕ σε ΛΟΓΙΚΗ μεταβλητή/στοιχείο πίνακα (με σειρά
 * εμφάνισης — κύριο πρόγραμμα πρώτα, μετά κάθε υποπρόγραμμα), ή `null` αν
 * δεν υπάρχει καμία. Κάθε υποπρόγραμμα ελέγχεται με τις ΔΙΚΕΣ ΤΟΥ δηλώσεις
 * (αυστηρά τοπική εμβέλεια — βλ. `findUndeclaredGlossaReference`).
 */
export function findReadBooleanTarget(program: GlossaProgram): ReadBooleanTarget | null {
  const mainResult = checkStatements(program.body, program.declarations);
  if (mainResult !== null) return mainResult;

  for (const sub of program.subprograms) {
    const subResult = checkStatements(sub.body, sub.declarations);
    if (subResult !== null) return subResult;
  }
  return null;
}
